import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Header, Button, Icon, Message, Segment } from 'semantic-ui-react';
import ManagementHeader from '../../../common/components/ManagementHeader';
import { useSharedData, useCharacterZone } from '../../hooks/core/useZoneHooks';
import { NestedLitGroupsBooksListEnh } from '../lists';
import CharactersListEnh from '../lists/CharactersListEnh-v2';
import EnhancedCharactersDiagnostic from '../diagnostics/EnhancedCharactersDiagnostic';
import {
  fetchOpportunities,
  fetchBookOpportunities,
  selectOpportunities,
  selectOpportunitiesStatus,
  selectOpportunitiesError,
  selectBookOpportunities
} from '../../../slices/opportunityDataSlice';

/**
 * OpportunityManagement-enh component
 * Enhanced opportunity management with integrated Book Management tools
 * Uses NestedLitGroupsBooksListEnh for consistent Literary Group/Book selection
 * @returns {JSX.Element} OpportunityManagement-enh component
 */
const OpportunityManagementEnh = () => {
  const dispatch = useDispatch();
  const sharedData = useSharedData();
  const characterZone = useCharacterZone();

  // Extract data from shared data hook
  const {
    litGroups,
    books,
    selections
  } = sharedData;

  // Get enhanced characters from character zone (not shared data)
  const characters = characterZone.characters;

  // Get opportunities from Redux
  const opportunities = useSelector(selectOpportunities);
  const opportunitiesStatus = useSelector(selectOpportunitiesStatus);
  const opportunitiesError = useSelector(selectOpportunitiesError);

  // Get current selections
  const selectedLitGroup = litGroups.find(lg => lg.uuid === selections.litGroupId) || null;
  const selectedBook = books.find(b => b.uuid === selections.bookId) || null;

  // Get functions from shared data and character zone
  const fetchCharacters = sharedData.actions.fetchCharacters;
  const setCharacterSelection = characterZone.actions.selectCharacter;

  // State for messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // State for import functionality
  const [selectedCharactersForImport, setSelectedCharactersForImport] = useState([]);

  // State for diagnostic modal
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState(null);

  // Handle book selection from NestedLitGroupsBooksListEnh
  const handleBookSelect = useCallback((book) => {
    console.log('OpportunityManagement: Book selected:', book.name);

    // Fetch characters for this book
    fetchCharacters(book.uuid);

    // Fetch opportunities for this book
    dispatch(fetchOpportunities({ bookId: book.uuid }));
  }, [fetchCharacters, dispatch]);

  // Auto-fetch characters when book selection changes
  useEffect(() => {
    if (selectedBook && selectedBook.uuid) {
      console.log('OpportunityManagement: Auto-fetching enhanced characters for book:', selectedBook.name);
      sharedData.actions.fetchCharactersEnhanced(selectedBook.uuid);
      dispatch(fetchOpportunities({ bookId: selectedBook.uuid }));
    }
  }, [selectedBook, sharedData.actions, dispatch]);

  // Handle character selection using the proven methodology from BookForm-enh.jsx
  const handleCharacterSelect = useCallback((character) => {
    if (character && character.uuid) {
      setCharacterSelection(character.uuid);
      console.log('OpportunityManagement: Character selected:', character.name);
    }
  }, [setCharacterSelection]);

  // Handle selected characters change for import
  const handleSelectedCharactersChange = useCallback((selectedCharacterIds) => {
    setSelectedCharactersForImport(selectedCharacterIds);
    console.log('OpportunityManagement: Selected characters for import:', selectedCharacterIds);
  }, []);

  // Filter opportunities for the selected book
  const bookOpportunities = useMemo(() => {
    if (!selectedBook || !opportunities) return [];
    return opportunities.filter(opp => opp.book?.uuid === selectedBook.uuid);
  }, [opportunities, selectedBook]);

  // Combine characters with their opportunities
  const characterOpportunityData = useMemo(() => {
    if (!characters || !selectedBook) return [];

    return characters.map(character => {
      // Find opportunities for this character
      const characterOpportunities = bookOpportunities.filter(
        opp => opp.character?.uuid === character.uuid
      );

      return {
        character,
        opportunities: characterOpportunities,
        hasOpportunities: characterOpportunities.length > 0
      };
    });
  }, [characters, bookOpportunities, selectedBook]);

  // Handle bulk create opportunities
  const handleBulkCreateOpportunities = useCallback(() => {
    if (!selectedBook) {
      setErrorMessage('Please select a book first');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    const charactersToImport = selectedCharactersForImport.length > 0
      ? selectedCharactersForImport
      : characterOpportunityData.map(data => data.character.uuid);

    console.log('OpportunityManagement: Creating opportunities for characters:', charactersToImport);

    // TODO: Implement bulk opportunity creation for selected characters
    setSuccessMessage(`Bulk opportunity creation will be implemented for ${charactersToImport.length} characters`);
    setTimeout(() => setSuccessMessage(''), 3000);
  }, [selectedBook, selectedCharactersForImport, characterOpportunityData]);

  return (
    <Grid>
      <Grid.Row>
        {/* Left Column: Literary Groups & Books List */}
        <Grid.Column width={6}>
          <NestedLitGroupsBooksListEnh
            onBookSelect={handleBookSelect}
            onNewBook={null} // Disable new book creation in opportunity context
          />
        </Grid.Column>

        {/* Right Column: Character Opportunities */}
        <Grid.Column width={10}>
          {successMessage && (
            <Message positive>
              <Message.Header>Success</Message.Header>
              <p>{successMessage}</p>
            </Message>
          )}

          {opportunitiesError && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{typeof opportunitiesError === 'string' ? opportunitiesError : (opportunitiesError.error || opportunitiesError.message || JSON.stringify(opportunitiesError))}</p>
            </Message>
          )}

          {/* Book Overview Header */}
          {selectedBook ? (
            <div>
              <Header as="h3">
                <Icon name="book" />
                <Header.Content>
                  {selectedBook.name}
                  <Header.Subheader>
                    {characterOpportunityData.length} characters • {bookOpportunities.length} opportunities
                  </Header.Subheader>
                </Header.Content>
              </Header>

              {/* Action Buttons */}
              <div style={{ marginTop: '1em', display: 'flex', gap: '1em', alignItems: 'center' }}>
                <Button
                  primary
                  icon
                  labelPosition="left"
                  onClick={handleBulkCreateOpportunities}
                  disabled={!selectedBook || characterOpportunityData.length === 0}
                >
                  <Icon name="add" />
                  {selectedCharactersForImport.length > 0
                    ? `Import Selected (${selectedCharactersForImport.length})`
                    : 'Import All Characters'
                  }
                </Button>

                {/* Diagnostic Button */}
                <Button
                  color="orange"
                  icon
                  labelPosition="left"
                  onClick={() => {
                    setDiagnosticData({
                      apiResponse: characterZone.rawApiResponse, // Get the stored raw API response
                      characters: characters,
                      bookId: selectedBook.uuid,
                      bookName: selectedBook.name
                    });
                    setShowDiagnostic(true);
                  }}
                  disabled={!characters || characters.length === 0}
                >
                  <Icon name="bug" />
                  API Diagnostic
                </Button>
              </div>
            </div>
          ) : (
            <Segment placeholder>
              <Header icon>
                <Icon name="search" />
                Select a book to manage opportunities
              </Header>
              <p>Choose a literary group and book from the list on the left to get started.</p>
            </Segment>
          )}

          {/* Enhanced Characters List with Opportunity Integration */}
          {selectedBook && (
            <div style={{ marginTop: '20px' }}>
              <Header as="h3" dividing>
                <Icon name="users" />
                <Header.Content>
                  Characters & Opportunities
                  <Header.Subheader>
                    Manage characters and their voice acting opportunities
                  </Header.Subheader>
                </Header.Content>
              </Header>

              <CharactersListEnh
                onCharacterSelect={handleCharacterSelect}
                showImportCheckboxes={true}
                onSelectedCharactersChange={handleSelectedCharactersChange}
                showOpportunityStatus={true}
                opportunityData={characterOpportunityData}
                enableBulkActions={true}
                selectedBook={selectedBook}
              />
            </div>
          )}
        </Grid.Column>
      </Grid.Row>

      {/* Diagnostic Modal */}
      <EnhancedCharactersDiagnostic
        isOpen={showDiagnostic}
        onClose={() => setShowDiagnostic(false)}
        apiResponse={diagnosticData?.apiResponse}
        characters={diagnosticData?.characters || []}
        title={`Enhanced Characters Diagnostic - ${diagnosticData?.bookName || 'Unknown Book'}`}
      />
    </Grid>
  );
};

export default OpportunityManagementEnh;
